import { UiKit } from '@datavant/uikit';
import React, { useContext, useEffect, useState } from 'react';
import useUser from '../lib/useUser';
import { MultiTenantContext } from '../lib/contexts/MultiTenantContext';
import { useRouter } from 'next/router';
import { Permission } from '../lib/permissions';
import { useUserPermissions } from '../lib/contexts/PermissionsProviderContext';
import { TenantOption, TenantSearch } from './Tenant/SearchTenant';

interface MultiTenantModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const defaultTenant = { label: '- Default User Tenant -', value: null };

export const MultiTenantModal = (props: MultiTenantModalProps) => {
    const router = useRouter();
    const {
        userPermissions: { CAN_SWITCH_TENANTS: canSwitchTenants },
    } = useUserPermissions([Permission.CAN_SWITCH_TENANTS]);
    const currentUser = useUser().user;
    const multiTenantContext = useContext(MultiTenantContext);
    const [tenantVal, setTenantVal] = useState<TenantOption | null>(defaultTenant);
    const [datavanter, setDatavanter] = useState<boolean>(currentUser?.isDatavanter ?? false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (multiTenantContext.tenantId) {
            setTenantVal({ label: multiTenantContext.tenantName, value: multiTenantContext.tenantId });
            setDatavanter(multiTenantContext.datavanter);
        }
    }, []);

    const modalBody = (
        <UiKit.Chakra.VStack key={'multi-tenant-body'} align={'left'} width={'400px'}>
            <TenantSearch
                label="Switch between tenants"
                description="(Start typing to see results...)"
                value={tenantVal}
                onChange={val => {
                    setTenantVal(val);
                    setIsLoading(false);
                }}
                onInputChange={input => {
                    if (input.length) setIsLoading(true);
                }}
                defaultOptions={[defaultTenant]}
            />
            <UiKit.Chakra.HStack padding={UiKit.consistency.buffer + ' 0px'}>
                <UiKit.Text.BodyRegular>Datavanter </UiKit.Text.BodyRegular>
                <UiKit.Form.Toggle
                    isOn={datavanter}
                    onChange={(isChecked: boolean) => {
                        setDatavanter(isChecked);
                    }}
                    isDisabled={tenantVal?.value === null}
                />
            </UiKit.Chakra.HStack>
        </UiKit.Chakra.VStack>
    );

    const modalFooter = (
        <UiKit.Chakra.HStack key={'multi-tenant-body'}>
            <UiKit.Button.Primary
                text={'Apply'}
                onClick={() => {
                    multiTenantContext.setValue({
                        tenantId: tenantVal?.value ?? undefined,
                        tenantName: tenantVal?.label?.split('\n')[0] ?? undefined,
                        datavanter: tenantVal?.value ? datavanter : false,
                    });
                    props.onClose();
                    router.reload();
                }}
                isLoading={isLoading}
                disabled={tenantVal === null}
            />
        </UiKit.Chakra.HStack>
    );

    const shouldShowModal = currentUser && canSwitchTenants;

    return shouldShowModal ? (
        <UiKit.Modal
            bodyFragment={modalBody}
            footerFragment={modalFooter}
            title={'Switch Tenant'}
            onClose={props.onClose}
            isOpen={props.isOpen}
            size={'lg'}
        />
    ) : null;
};
